import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "../components/AuthLayout";
import {
  APPLICATION_NOT_ENTITLED,
  TENANT_SUSPENDED,
  setTenantAccessWithdrawnHandler,
  type TenantAccessWithdrawal,
} from "../lib/api";
import { formatDocumentTitle } from "../lib/documentTitle";
import { chooseTenant } from "./tenantSelection";

// The two refusals that are about the ORGANISATION rather than about the reader, as the people inside it
// meet them (decision f3ca7f9e + task e4ad250d for the freeze; ADR-0014 §7.1 step 3 for the entitlement).
//
// The backend's half of each is one line in TenantResolutionMiddleware: every `/api` and `/scorm` request
// is refused 403 with a machine-readable code, whoever the caller is — `tenant_suspended` when the tenant
// is frozen (TenantSuspensionGate), `application_not_entitled` when the tenant has not bought the
// application the request is addressed to (ApplicationEntitlementGate). This is the other half — each code
// rendered in the reader's own language, because the backend holds no message catalogue and its English
// `detail` is written for a log, not a person.
//
// WHY A TERMINAL VIEW, AND WHY IT SITS ABOVE THE ROUTER
// -----------------------------------------------------
// Not a banner over a working page, and not a per-page error state. NOTHING tenant-scoped answers in
// either state, so every surface behind a banner would be an empty page with an explanation on top of it,
// and there is nowhere to send the reader instead — no other route of this tenant works either. So the
// first such refusal from anywhere replaces the whole SPA with one screen, in the shape LoginPage's
// "Organisaatiota ei löytynyt" state already established for a terminal pre-session state: the AuthLayout
// card, a heading, the explanation as `role="alert"`, and NO action — no sign-in button, no retry, no link
// home.
//
// WHY ONE GATE AND TWO SCREENS
// ----------------------------
// The MECHANISM is identical, down to the reason it has to be terminal, so there is one gate and one hook:
// a second gate nested around the router would be a second copy of this file's whole argument, and the
// wiring rule below could then be satisfied by whichever of the two was still mounted. The SENTENCE is not
// identical and must not be shared — see each view.
//
// WHY NEITHER IS A SESSION EXPIRY
// -------------------------------
// The tempting shortcut is to let the shared 401 path swallow them, since the person does end up outside
// the app. It would loop: the session is valid, so clearing it sends them to /login, where they sign in
// successfully and meet the identical 403 on the next call. `TenantSuspendedError` and
// `ApplicationNotEntitledError` exist precisely so these drive this view instead (src/lib/api.ts).

/**
 * The suspension screen — the tenant is frozen and a `/resume` puts it back.
 *
 * WHAT IT MAY NOT SAY. Not "your account was removed" and not "you are not provisioned in this
 * organisation" — neither is true, both send the reader to their pääkäyttäjä to have an account re-created
 * that already exists. And NOT the operator's suspension reason: that note is written for the platform
 * console and the audit chain, it is not served to this surface at all, and a frozen tenant's users are
 * not its audience.
 */
export function TenantSuspendedView() {
  return (
    <TerminalCard
      testId="tenant-suspended"
      titleKey="states.tenantSuspended"
      bodyKey="auth.tenantSuspendedBody"
      contactKey="auth.tenantSuspendedContact"
    />
  );
}

/**
 * The entitlement screen — the organisation has not bought this application (ADR-0014 §7.1 step 3).
 *
 * WHAT IT MAY NOT SAY, and why it is not the suspension's copy. "Access is suspended" would be false and
 * expensively so: it describes something that was taken away and can be given back by a resume, and it
 * would send a pääkäyttäjä chasing an outage that is not one. Nor may it read as a ROLE refusal ("ei
 * käyttöoikeutta", "access denied"): that is the one sentence an administrator of this tenant would act on
 * by looking for a role to grant, and no role in this tenant can overcome a purchase that was not made.
 * What it says is the commercial fact, that nothing is wrong with the reader's account, and who to ask.
 */
export function TenantNotEntitledView() {
  return (
    <TerminalCard
      testId="tenant-not-entitled"
      titleKey="states.tenantNotEntitled"
      bodyKey="auth.tenantNotEntitledBody"
      contactKey="auth.tenantNotEntitledContact"
    />
  );
}

/**
 * The card both screens are: heading, the explanation as one `role="alert"`, and nothing to press. Shared
 * because the SHAPE is the decision — terminal, no navigation — while every string is the caller's.
 */
function TerminalCard({
  testId,
  titleKey,
  bodyKey,
  contactKey,
}: {
  testId: string;
  titleKey: string;
  bodyKey: string;
  contactKey: string;
}) {
  const { t } = useTranslation();
  const title = t(titleKey);
  const appName = t("app.title");

  // The browser tab, written here rather than through the route title channel (src/app/pageTitle):
  // this view stands in place of the router, so no route is matched any more and the tab would
  // otherwise keep naming the page the reader was on when the refusal was discovered.
  useEffect(() => {
    document.title = formatDocumentTitle(title, appName);
  }, [title, appName]);

  return (
    <AuthLayout>
      <div className="auth__head">
        <h1 className="auth__title" data-testid={testId}>
          {title}
        </h1>
        <div role="alert">
          <p className="auth__text">{t(bodyKey)}</p>
          <p className="auth__text">{t(contactKey)}</p>
        </div>
      </div>
    </AuthLayout>
  );
}

/**
 * Registers the withdrawal hook and, once it fires, renders the view for its REASON in place of its
 * children. Mounted once, around the router (src/main.tsx).
 *
 * One-way on purpose: there is no path back from here in this document. A resume is an operator action
 * minutes or days away, and a purchase is longer still; polling for either would mean asking a tenant that
 * refuses every request whether it still refuses. A page load afterwards is the way back in — which is
 * what a person does anyway, and what the real-stack E2Es exercise.
 */
export function TenantAccessGate({ children }: { children: ReactNode }) {
  const [withdrawn, setWithdrawn] = useState<TenantAccessWithdrawal | null>(null);

  useEffect(() => {
    setTenantAccessWithdrawnHandler((reason) => {
      // ADR-0018 §6.3 — a CHOSEN tenant that answers one of these two cannot stay chosen. The screens
      // below are one-way by design (a resume is an operator action days away, so there is nothing to poll
      // and nothing to press), and for a person with memberships that would otherwise be a dead end rather
      // than a terminal state: the stored choice would pin them to the frozen organisation on every load
      // they ever made, with the other organisations they are a member of unreachable from inside the app.
      // Clearing it leaves THIS page terminal, exactly as before, and puts the page load the person makes
      // anyway back in front of the picker. For a person of one tenant nothing is stored, so nothing
      // changes.
      chooseTenant(null);
      setWithdrawn(reason);
    });
    return () => setTenantAccessWithdrawnHandler(() => {});
  }, []);

  if (withdrawn === TENANT_SUSPENDED) return <TenantSuspendedView />;
  if (withdrawn === APPLICATION_NOT_ENTITLED) return <TenantNotEntitledView />;
  return <>{children}</>;
}
